import { useEffect, useRef, useState } from 'react';
import EcoImg from 'eco-design/base/eco-img';
import css from './index.module.scss';
import { useLang } from '@/i18n';
import getLocal from '@/app/utils/i18n';
import { useWebpOrJpg } from '../../hooks/useWebpOrJpg';

const AutoSave24Ani: React.FC = () => {
    const { t } = useLang('dpux.AutoSave24Ani');
    const { t: t2 } = useLang('dpux');
    const { locale = 'us' } = getLocal();
    const webpOrJpg = useWebpOrJpg('#delta-pro-ultra-x-AutoSave24Ani');

    /** 数据 */
    const list = [
        {
            img: 'https://websiteoss.ecoflow.com/upload/image/1757382154756/7%20Money%20Saver_01.jpg',
            title: t('item1_title'),
            desc: t('item1_desc'),
        },
        {
            img: 'https://websiteoss.ecoflow.com/upload/image/1757382154461/7%20Money%20Saver_02.jpg',
            title: t('item2_title'),
            desc: t.rich('item2_desc', {
                sup: (v: string) => <sup>{v}</sup>,
            }),
        },
        {
            img: 'https://websiteoss.ecoflow.com/upload/image/1757382154297/7%20Money%20Saver_03.jpg',
            title: t('item3_title'),
            desc: t('item3_desc'),
        },
    ];

    /** 动画 */
    const sectionRef = useRef<HTMLDivElement>(null);
    const maskRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const progressRef = useRef<HTMLDivElement>(null);
    const stickyRef = useRef<HTMLDivElement>(null);
    const progressBarRef = useRef<HTMLDivElement>(null);
    const [enterProgress, setEnterProgress] = useState(false);
    const [status, setStatus] = useState(0);
    useEffect(() => {
        if (!maskRef.current || !footerRef.current || !stickyRef.current) return;
        const AniArr: any = [];
        const { height } = stickyRef.current.getBoundingClientRect();
        const stickyTop = height - window.innerHeight;
        let curGap = stickyTop < 0 ? 0 : -stickyTop;

        if (window.innerWidth < 1024 && window.innerWidth >= 768) {
            curGap = 100;
        } else if (window.innerWidth < 768) {
            curGap = stickyTop < 0 ? 0 : -stickyTop;
        }
        stickyRef.current.setAttribute('style', `top: ${curGap}px;`);
        if (window.innerWidth < 768) {
            setEnterProgress(true);
            AniArr.push(
                AniContainer.create({
                    triggerElement: `.delta-pro-ultra-x-AutoSave24Ani .AutoSave24Ani-progress`,
                    triggerHook: 0,
                    offset: curGap,
                    duration: '300%',
                    onProcess: (process: number) => {
                        if (process >= 0.999) {
                            setStatus(2);
                        } else if (process >= 0.5 && process < 0.999) {
                            setStatus(1);
                        } else if (process >= 0.001 && process < 0.5) {
                            setStatus(0);
                        } else {
                            setStatus(-1);
                        }
                    },
                    asyncFromTo: [
                        [`.AutoSave24Ani-progress`, { x: 0 }, { x: window.innerWidth - 778 - 48 }, 3],
                        [`.delta-pro-ultra-x-AutoSave24Ani .list`, { x: 0 }, { x: window.innerWidth - 778 - 48 }, 3],
                        [`.AutoSave24Ani-footer`, { x: 0 }, { x: window.innerWidth - 778 - 48 }, 3],
                        [`.AutoSave24Ani-progress  .progress-bar .mask`, { y: 0, x: maskRef.current.offsetWidth }, { y: 0, x: maskRef.current.offsetWidth }, 0],
                    ],
                    asyncTimeLine: [0, 0, 0, 3],
                }),
            );
        } else {
            AniArr.push(
                AniContainer.create({
                    triggerElement: `.delta-pro-ultra-x-AutoSave24Ani .AutoSave24Ani-progress`,
                    triggerHook: 0,
                    offset: -curGap,
                    duration: '300%',
                    onProcess: (process: number) => {
                        if (process <= 0) {
                            setEnterProgress(false);
                        }
                        if (process >= 0.999) {
                            setStatus(2);
                        } else if (process >= 0.5) {
                            setStatus(1);
                        } else if (process >= 0.001) {
                            setStatus(0);
                        } else {
                            setStatus(-1);
                        }
                    },
                    onEnter: () => {
                        setEnterProgress(true);
                    },
                    asyncFromTo: [
                        [`.AutoSave24Ani-progress  .progress-bar .mask`, { x: 0 }, { x: maskRef.current.offsetWidth }, 3],
                        [`.AutoSave24Ani-footer`, { x: 0 }, { x: -footerRef.current?.getBoundingClientRect().left }, 3],
                        [`.AutoSave24Ani-progress  .progress-bar .mask`, { y: 0, x: maskRef.current.offsetWidth }, { y: 0, x: maskRef.current.offsetWidth }, 0],
                    ],
                    asyncTimeLine: [0, 0, 3],
                }),
            );
        }

        const label = sectionRef.current.querySelector('.label');
        const title = sectionRef.current.querySelector('.title');
        const desc = sectionRef.current.querySelector('.desc');
        AniArr.push(
            AniContainer.create({
                triggerElement: sectionRef.current,
                triggerHook: 1,
                offset: '40%',
                duration: '100%',
                onEnter: () => {
                    label?.classList.add('enter');
                    title?.classList.add('enter');
                    desc?.classList.add('enter');
                },
                onProcess: (process: number) => {
                    if (process <= 0) {
                        label?.classList.remove('enter');
                        title?.classList.remove('enter');
                        desc?.classList.remove('enter');
                    }
                },
            }),
        );

        return () => {
            AniArr.forEach((i) => i?.destroy?.());
        };
    }, []);

    return (
        <section
            className={`${css.section} delta-pro-ultra-x-AutoSave24Ani GtmModuleData-section`}
            id="delta-pro-ultra-x-AutoSave24Ani"
            data-section-id={'delta-pro-ultra-x-AutoSave24Ani'}
            data-section-title={t('title')}
            ref={sectionRef}
        >
            <div className="AutoSave24Ani-sticky" ref={stickyRef}>
                <div className="AutoSave24Ani-container">
                    <div className="label ani-1">{t('label')}</div>
                    <div className="title ani-2">{t('title')}</div>
                    <div className="desc ani-3">{t('desc')}</div>
                    <div className="AutoSave24Ani-progress" ref={progressRef}>
                        <div className={`progress-icon status-${status}`}>
                            <EcoImg pc="https://websiteoss.ecoflow.com/upload/image/1758340337120/moon.svg" isOpenLazy />
                            <EcoImg pc="https://websiteoss.ecoflow.com/upload/image/1758340336978/sun.svg" isOpenLazy />
                            <EcoImg pc="https://websiteoss.ecoflow.com/upload/image/1758340337120/moon.svg" isOpenLazy />
                        </div>
                        <div className={`progress-bar ${enterProgress ? 'active' : ''}`} ref={progressBarRef}>
                            <div className="mask" ref={maskRef}></div>
                        </div>
                    </div>
                    <div className={`list status-${status}`}>
                        {list.map((i, idx) => (
                            <div className="list-item" key={idx}>
                                <div className="list-title">{i.title}</div>
                                <div className="list-desc">{i.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="AutoSave24Ani-footer" ref={footerRef}>
                    <EcoImg
                        className="footer-item"
                        pc={webpOrJpg(
                            locale === 'us'
                                ? 'https://websiteoss.ecoflow.com/upload/image/1760423745730/AutoSave24Ani-1.jpg'
                                : 'https://websiteoss.ecoflow.com/upload/image/1760425898436/pc_1_ca.jpg',
                            locale === 'us'
                                ? 'https://websiteoss.ecoflow.com/upload/image/1760423745428/AutoSave24Ani-1.webp'
                                : 'https://websiteoss.ecoflow.com/upload/image/1760425898142/pc_1_ca.webp',
                        )}
                        h5={webpOrJpg(
                            locale === 'us'
                                ? 'https://websiteoss.ecoflow.com/upload/image/1760423744976/AutoSave24Ani-1-m.jpg'
                                : 'https://websiteoss.ecoflow.com/upload/image/1760425897978/m_1_ca.jpg',
                            locale === 'us'
                                ? 'https://websiteoss.ecoflow.com/upload/image/1760423745215/AutoSave24Ani-1-m.webp'
                                : 'https://websiteoss.ecoflow.com/upload/image/1760425898857/m_1_ca.webp',
                        )}
                    />
                    <EcoImg
                        className="footer-item"
                        pc={webpOrJpg(
                            'https://websiteoss.ecoflow.com/upload/image/1759211539286/Auto2.jpg',
                            'https://websiteoss.ecoflow.com/upload/image/1759211539929/Auto2.webp',
                        )}
                        h5={webpOrJpg(
                            'https://websiteoss.ecoflow.com/upload/image/1759211761816/Auto2m.jpg',
                            'https://websiteoss.ecoflow.com/upload/image/1759211762542/Auto2m.webp',
                        )}
                    />
                    <EcoImg
                        className="footer-item"
                        pc={webpOrJpg(
                            'https://websiteoss.ecoflow.com/upload/image/1759211539505/Auto3.jpg',
                            'https://websiteoss.ecoflow.com/upload/image/1759211540207/Auto3.webp',
                        )}
                        h5={webpOrJpg(
                            'https://websiteoss.ecoflow.com/upload/image/1759211762086/Auto3m.jpg',
                            'https://websiteoss.ecoflow.com/upload/image/1759211762716/Auto3m.webp',
                        )}
                    />
                </div>
            </div>
        </section>
    );
};

export default AutoSave24Ani;
